import React, {
  useMemo,
  useEffect,
} from 'react';
import Controller from 'core/components/FormController';
import {
  useForm,
} from 'react-hook-form';
import {
  formatDate,
} from 'utils/date';
import Dialog from 'pages/Dashboard/components/Dialog';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  useGetAddendum,
  useUpsertAddendum,
} from 'pages/Dashboard/pages/Charts/pages/Details/hooks';
import {
  AddendumDTO,
  PatientVisitDTO,
} from 'dtos';
import isNil from 'lodash/isNil';
import TextArea from 'core/components/TextArea';
import {
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export type AddendumDialogProps = {
  visitInfo: PatientVisitDTO;
};

type Props = AddendumDialogProps & {
  onClose: () => void;
};

export default function AddendumDialog({
  onClose,
  visitInfo,
}: Props) {
  const {
    control,
    reset,
    formState: { isValid, isSubmitting },
    handleSubmit,
  } = useForm<AddendumDTO>({ mode: 'all' });
  const { mutateAsync: upsertAddendum } = useUpsertAddendum();
  const {
    data,
    isFetching,
  } = useGetAddendum(visitInfo?.visitNoteId ?? 0);
  const userInfo = useUserInfo();
  const signature = useMemo(() => (
    `${userInfo?.user?.formattedNames?.fullNameWithCredentials} ${formatDate(Date.now())}`
  ), [userInfo]);

  const onConfirm = async ({ content }: AddendumDTO) => {
    await upsertAddendum({
      visitNoteBaseId: visitInfo.visitNoteId ?? 0,
      content,
    });
    visitNoteEventChannel.emit('onAddendumPosted', visitInfo?.encounterId);
    onClose();
  };

  useEffect(() => {
    if (!isFetching && !isNil(data)) {
      reset(data);
    }
  }, [data]);

  return (
    <Dialog
      open
      title="Addendum"
      onClose={onClose}
      className="w-[34rem]"
      disableConfirmButton={!isValid || isSubmitting}
      onConfirm={handleSubmit(onConfirm)}
      confirmText={signature}
      showActions
    >
      <Controller
        control={control}
        name="content"
        rules={{ required: true }}
        render={({ field, fieldState: { invalid } }) => (
          <TextArea
            id="addendum"
            {...field}
            value={field.value ?? ''}
            error={invalid}
            disabled={isSubmitting}
            actions={['dictation']}
            classes={{
              root: 'w-full !min-h-[180px] !max-h-[280px]',
              textarea: 'pb-0',
            }}
            trackingLabel="Addendum"
          />
        )}
      />
    </Dialog>
  );
}
